import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { errMsg } from '../api';
import { STAFF_ROLES, formatUGX } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import {
  Building2, Plus, UserPlus, Ban, CheckCircle2, Users, Loader2, MoreVertical,
  KeyRound, Pencil, Bell, Copy, Clock, TrendingUp,
} from 'lucide-react';

const daysLeft = (iso) => (iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null);

// ---------------- Super User: Platform console ----------------
const SuperUserAdmin = () => {
  const { toast } = useToast();
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', district: '', licence: '', phone: '', plan: 'trial', trial_days: 30, monthly_fee: '', admin_name: '', admin_email: '', admin_password: '' });
  const [editTarget, setEditTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPharmacies((await api.get('/admin/pharmacies')).data); }
    catch (e) { toast({ title: 'Could not load pharmacies', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const addPharmacy = async () => {
    if (!form.name || !form.admin_email || !form.admin_password) {
      toast({ title: 'Missing info', description: 'Pharmacy name, admin email and password are required.', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      await api.post('/admin/pharmacies', { ...form, monthly_fee: Number(form.monthly_fee) || 0, trial_days: Number(form.trial_days) || 30 });
      toast({ title: 'Pharmacy onboarded', description: `${form.name} is live.` });
      setForm({ name: '', district: '', licence: '', phone: '', plan: 'trial', trial_days: 30, monthly_fee: '', admin_name: '', admin_email: '', admin_password: '' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Could not create pharmacy', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (ph) => {
    const status = ph.status === 'active' ? 'suspended' : 'active';
    try { await api.patch(`/admin/pharmacies/${ph.id}/status`, { status }); toast({ title: status === 'suspended' ? 'Pharmacy suspended' : 'Pharmacy reactivated', description: ph.name }); load(); }
    catch (e) { toast({ title: 'Action failed', description: errMsg(e), variant: 'destructive' }); }
  };

  const resetPw = async (ph) => {
    try {
      const res = await api.post(`/admin/pharmacies/${ph.id}/reset-password`, {});
      setResetResult({ name: ph.name, email: res.data.admin_email, password: res.data.new_password });
    } catch (e) { toast({ title: 'Reset failed', description: errMsg(e), variant: 'destructive' }); }
  };

  const remind = async (ph) => {
    try { await api.post(`/admin/pharmacies/${ph.id}/remind`); toast({ title: 'Reminder recorded', description: `${ph.name} — note: email/SMS delivery is not wired yet.` }); load(); }
    catch (e) { toast({ title: 'Action failed', description: errMsg(e), variant: 'destructive' }); }
  };

  // KPIs
  const active = pharmacies.filter((p) => p.status === 'active').length;
  const suspended = pharmacies.filter((p) => p.status === 'suspended').length;
  const trialsEnding = pharmacies.filter((p) => p.plan === 'trial' && p.trial_ends_at && daysLeft(p.trial_ends_at) <= 7).length;
  const mrr = pharmacies.filter((p) => p.plan === 'active' && p.status === 'active').reduce((s, p) => s + (p.monthly_fee || 0), 0);

  const KPI = ({ label, value, Icon, tone }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${tone}`}><Icon size={22} /></div>
      <div><div className="font-display font-extrabold text-3xl text-slate-900">{value}</div><div className="text-slate-500 text-sm">{label}</div></div>
    </div>
  );

  const planBadge = (p) => {
    if (p.plan === 'trial') {
      const d = daysLeft(p.trial_ends_at);
      const ending = d !== null && d <= 7;
      return <Badge variant="outline" className={ending ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}>Trial · {d !== null ? `${d}d left` : ''}</Badge>;
    }
    return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Active{p.monthly_fee ? ` · ${formatUGX(p.monthly_fee)}/mo` : ''}</Badge>;
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Super Admin</h1>
          <p className="text-slate-500 mt-2 text-[15px]">Manage every pharmacy subscribing to the Vitality platform.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11 px-5 font-semibold gap-2"><Plus size={18} /> Onboard pharmacy</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-xl">Onboard a new pharmacy</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-slate-700">Pharmacy name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Jinja Health Pharmacy" className="mt-1.5 h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-700">District</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">NDA licence</Label><Input value={form.licence} onChange={(e) => setForm({ ...form, licence: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-700">Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="trial">Trial</SelectItem><SelectItem value="active">Active</SelectItem></SelectContent>
                  </Select>
                </div>
                {form.plan === 'trial'
                  ? <div><Label className="text-slate-700">Trial days</Label><Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} className="mt-1.5 h-11" /></div>
                  : <div><Label className="text-slate-700">Monthly fee</Label><Input type="number" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} placeholder="UGX" className="mt-1.5 h-11" /></div>}
                <div><Label className="text-slate-700">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-700 mb-2">Pharmacy Admin login</p>
                <div className="space-y-3">
                  <div><Label className="text-slate-700">Admin full name</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-700">Admin email</Label><Input value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="mt-1.5 h-11" /></div>
                    <div><Label className="text-slate-700">Temp password</Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="min 8 chars" className="mt-1.5 h-11" /></div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={addPharmacy} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Creating…' : 'Onboard pharmacy'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
        <KPI label="Active tenants" value={active} Icon={CheckCircle2} tone="bg-green-50 text-green-600" />
        <KPI label="Trials ending soon" value={trialsEnding} Icon={Clock} tone="bg-amber-50 text-amber-600" />
        <KPI label="Suspended" value={suspended} Icon={Ban} tone="bg-red-50 text-red-600" />
        <KPI label="Monthly recurring" value={formatUGX(mrr)} Icon={TrendingUp} tone="bg-blue-50 text-blue-600" />
      </div>

      {/* Tenants table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-8">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-slate-900">Tenants</h3>
          <span className="text-slate-500 text-sm">{pharmacies.length} pharmacies</span>
        </div>
        {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[15px] min-w-[840px]">
              <thead><tr className="text-left text-slate-500 text-sm border-b border-slate-100">
                <th className="px-6 py-3 font-semibold">Pharmacy</th>
                <th className="px-6 py-3 font-semibold">Plan</th>
                <th className="px-6 py-3 font-semibold">Users</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {pharmacies.map((ph) => (
                  <tr key={ph.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><Building2 size={18} /></div>
                        <div>
                          <div className="font-display font-bold text-slate-900">{ph.name}</div>
                          <div className="text-slate-400 text-xs">{[ph.district, ph.licence, ph.admin_email].filter(Boolean).join(' · ')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{planBadge(ph)}</td>
                    <td className="px-6 py-4 text-slate-600">{ph.users}</td>
                    <td className="px-6 py-4">{ph.status === 'active' ? <Badge className="bg-green-100 text-green-700 font-medium">Active</Badge> : <Badge className="bg-red-100 text-red-700 font-medium">Suspended</Badge>}</td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg"><MoreVertical size={16} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setEditTarget({ ...ph, monthly_fee: ph.monthly_fee || '', trial_days: 30 })}><Pencil size={15} className="mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => resetPw(ph)}><KeyRound size={15} className="mr-2" /> Reset password</DropdownMenuItem>
                          {ph.plan === 'trial' && <DropdownMenuItem onClick={() => remind(ph)}><Bell size={15} className="mr-2" /> Send reminder</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          {ph.status === 'active'
                            ? <DropdownMenuItem onClick={() => toggleStatus(ph)} className="text-red-600 focus:text-red-600"><Ban size={15} className="mr-2" /> Suspend</DropdownMenuItem>
                            : <DropdownMenuItem onClick={() => toggleStatus(ph)} className="text-green-600 focus:text-green-600"><CheckCircle2 size={15} className="mr-2" /> Reactivate</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {pharmacies.length === 0 && <tr><td colSpan={5} className="py-16 text-center text-slate-400">No pharmacies yet. Click "Onboard pharmacy" to add your first one.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditPharmacyDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />

      {/* Reset password result */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Password reset</DialogTitle></DialogHeader>
          {resetResult && (
            <div className="space-y-3">
              <p className="text-slate-500 text-sm">New admin password for <span className="font-semibold text-slate-800">{resetResult.name}</span>. Share it securely — it won't be shown again.</p>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-400 text-xs">Admin email</div>
                <div className="font-medium text-slate-800">{resetResult.email}</div>
                <div className="text-slate-400 text-xs mt-2">Temporary password</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono font-bold text-slate-900">{resetResult.password}</code>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { navigator.clipboard?.writeText(resetResult.password); toast({ title: 'Copied' }); }}><Copy size={14} /> Copy</Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setResetResult(null)} className="bg-blue-600 hover:bg-blue-700 text-white">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EditPharmacyDialog = ({ target, onClose, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (target) setForm({ name: target.name || '', district: target.district || '', licence: target.licence || '', phone: target.phone || '', plan: target.plan || 'active', monthly_fee: target.monthly_fee || '', trial_days: 30 }); }, [target]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pharmacies/${target.id}`, { ...form, monthly_fee: Number(form.monthly_fee) || 0, trial_days: Number(form.trial_days) || 30 });
      toast({ title: 'Pharmacy updated', description: form.name });
      onSaved();
    } catch (e) { toast({ title: 'Update failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl">Edit pharmacy</DialogTitle></DialogHeader>
        {form && (
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-700">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700">District</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label className="text-slate-700">NDA licence</Label><Input value={form.licence} onChange={(e) => setForm({ ...form, licence: e.target.value })} className="mt-1.5 h-11" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-700">Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="trial">Trial</SelectItem><SelectItem value="active">Active</SelectItem></SelectContent>
                </Select>
              </div>
              {form.plan === 'trial'
                ? <div><Label className="text-slate-700">Trial days</Label><Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} className="mt-1.5 h-11" /></div>
                : <div><Label className="text-slate-700">Monthly fee</Label><Input type="number" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} className="mt-1.5 h-11" /></div>}
              <div><Label className="text-slate-700">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11" /></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Saving…' : 'Save changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Pharmacy Admin: Staff management ----------------
const PharmacyAdminPanel = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'pharmacist', phone: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { setStaff((await api.get('/staff')).data); }
    catch (e) { toast({ title: 'Could not load team', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const addStaff = async () => {
    if (!form.name || !form.email || !form.password) { toast({ title: 'Missing info', description: 'Name, email and password are required.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await api.post('/staff', form);
      toast({ title: 'User added', description: `${form.name} can now sign in.` });
      setForm({ name: '', email: '', password: '', role: 'pharmacist', phone: '' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Could not add user', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    const status = u.status === 'active' ? 'inactive' : 'active';
    try { await api.patch(`/staff/${u.id}/status`, { status }); load(); }
    catch (e) { toast({ title: 'Action failed', description: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Team & Access</h1>
          <p className="text-slate-500 mt-2 text-[15px]">Add unlimited staff to your pharmacy and control their access.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11 px-5 font-semibold gap-2"><UserPlus size={18} /> Add user</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-xl">Add a team member</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-slate-700">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-700">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">Temp password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 chars" className="mt-1.5 h-11" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAFF_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-700">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={addStaff} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Adding…' : 'Add user'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-slate-900">Team members</h3>
          <span className="text-slate-500 text-sm">{staff.length} user(s)</span>
        </div>
        {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div> : (
          <div className="divide-y divide-slate-100">
            {staff.map((u) => {
              const initials = (u.name || 'U').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
              const isSelf = u.id === user?.id;
              return (
                <div key={u.id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors">
                  <div className="h-11 w-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-display font-bold shrink-0">{initials}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-slate-900">{u.name} {isSelf && <span className="text-slate-400 font-normal text-xs">(you)</span>}</div>
                    <div className="text-slate-500 text-sm">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                  </div>
                  <Badge variant="outline" className="border-slate-200 text-slate-600">{u.role_label}</Badge>
                  {u.status === 'active' ? <Badge className="bg-green-100 text-green-700 font-medium">Active</Badge> : <Badge className="bg-slate-200 text-slate-600 font-medium">Inactive</Badge>}
                  <Button variant="outline" onClick={() => toggleStatus(u)} disabled={isSelf}
                    className={`rounded-lg h-9 px-4 font-medium ${u.status === 'active' ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}>
                    {u.status === 'active' ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { user } = useAuth();
  if (user?.role === 'super_user') return <SuperUserAdmin />;
  return <PharmacyAdminPanel />;
};

export default AdminPage;
